import React, { createContext, useContext, useMemo, useState } from "react";
import { RaffleParticipant } from "@/apps/raffle/types";
import {
  buildSectors,
  CENTER,
  HORIZONTAL_SAFE_GAP,
  RIM_WIDTH,
  WHITE_RIM_WIDTH,
  WHEEL_SIZE,
} from "./wheelMath";

// Static context: heavy geometry that depends only on participants list.
// Changes only when participants array changes — NOT when currentParticipantIndex changes.
type RaffleWheelStaticContextValue = {
  scale: number;
  baseViewportScale: number;
  center: number;
  radius: number;
  wheelSize: number;
  normalWheelSize: number;
  sectors: ReturnType<typeof buildSectors>;
};

// Dynamic context: lightweight value that may change frequently (e.g. on scroll / tick).
// Isolated so that only components that need it will re-render.
type RaffleWheelDynamicContextValue = {
  currentParticipantIndex?: number;
};

type RaffleWheelRotationContextValue = {
  rotation: number;
};

const RaffleWheelStaticContext =
  createContext<RaffleWheelStaticContextValue | null>(null);
const RaffleWheelDynamicContext =
  createContext<RaffleWheelDynamicContextValue | null>(null);
const RaffleWheelRotationContext =
  createContext<RaffleWheelRotationContextValue | null>(null);

type ProviderProps = {
  participants: RaffleParticipant[];
  currentParticipantIndex?: number;
  rotation?: number;
  children: React.ReactNode;
};

export const RaffleWheelProvider = ({
  participants,
  currentParticipantIndex,
  rotation = 0,
  children,
}: ProviderProps) => {
  // Compute baseViewportScale once on mount — window.innerWidth doesn't change
  // between the provider mount and first paint, and ResizeObserver on WheelSurface
  // handles subsequent viewport changes if needed.
  const [baseViewportScale] = useState(() =>
    typeof window === "undefined"
      ? 1
      : Math.min(1, (window.innerWidth - HORIZONTAL_SAFE_GAP) / WHEEL_SIZE)
  );

  const scale = 1;
  const center = CENTER * scale;
  const radius = center - RIM_WIDTH - WHITE_RIM_WIDTH;
  const wheelSize = WHEEL_SIZE * scale * baseViewportScale;
  const normalWheelSize = WHEEL_SIZE * baseViewportScale;

  // buildSectors is O(n) and produces n path strings — memoize hard.
  const sectors = useMemo(() => buildSectors(participants), [participants]);

  const staticValue = useMemo<RaffleWheelStaticContextValue>(
    () => ({
      scale,
      baseViewportScale,
      center,
      radius,
      wheelSize,
      normalWheelSize,
      sectors,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      baseViewportScale,
      center,
      normalWheelSize,
      radius,
      sectors,
      wheelSize,
    ]
  );

  // Dynamic value: plain object, intentionally not memoized —
  // it should trigger a re-render in dynamic consumers when index changes.
  const dynamicValue = useMemo<RaffleWheelDynamicContextValue>(
    () => ({
      currentParticipantIndex,
    }),
    [currentParticipantIndex]
  );
  const rotationValue = useMemo<RaffleWheelRotationContextValue>(
    () => ({
      rotation,
    }),
    [rotation]
  );

  return (
    <RaffleWheelStaticContext.Provider value={staticValue}>
      <RaffleWheelDynamicContext.Provider value={dynamicValue}>
        <RaffleWheelRotationContext.Provider value={rotationValue}>
          {children}
        </RaffleWheelRotationContext.Provider>
      </RaffleWheelDynamicContext.Provider>
    </RaffleWheelStaticContext.Provider>
  );
};

/** Static geometry for wheel rendering. */
export const useRaffleWheelStatic = (): RaffleWheelStaticContextValue => {
  const context = useContext(RaffleWheelStaticContext);
  if (!context) {
    throw new Error(
      "useRaffleWheelStatic must be used within RaffleWheelProvider"
    );
  }
  return context;
};

/** Dynamic context. Use only where currentParticipantIndex is needed (highlight layer). */
export const useRaffleWheelDynamic = (): RaffleWheelDynamicContextValue => {
  const context = useContext(RaffleWheelDynamicContext);
  if (!context) {
    throw new Error(
      "useRaffleWheelDynamic must be used within RaffleWheelProvider"
    );
  }
  return context;
};

export const useRaffleWheelRotation = (): RaffleWheelRotationContextValue => {
  const context = useContext(RaffleWheelRotationContext);
  if (!context) {
    throw new Error(
      "useRaffleWheelRotation must be used within RaffleWheelProvider"
    );
  }
  return context;
};

/**
 * @deprecated Use useRaffleWheelStatic or useRaffleWheelDynamic instead.
 * Kept for backward compatibility — removes itself once all call sites are migrated.
 */
export const useRaffleWheel = () => ({
  ...useRaffleWheelStatic(),
  ...useRaffleWheelDynamic(),
  ...useRaffleWheelRotation(),
});
